/* eslint-disable indent */
import React from 'react';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { useMeasurement, useBinLevelMeasurementOverrides, useSirenEntity } from '~/utility/BinSentry-ui-utility';
import { useOrdersDialogStateSelector } from '../context/orders-dialog-context';
import { BarnFeedLevelDisplay } from '../../../../bins/barn-feed-level';
import { useBarnLatestFeedLevel, useBarnTotalWeight } from '../../../../bins/bin-level/use-barn-feed-levels';
import { useOrderVolume } from '../context/orders-dialog-context-utilities';

export const BIN_PROFILE_WIDTH_PX = 64;
export const BIN_PROFILE_HEIGHT_PX = 220;

export function BarnLevelDisplay () {
  const [selectedOrderId, selectedOrderEntity] = useOrdersDialogStateSelector([
    'selectedOrderId', 'selectedOrderEntity',
  ]);
  const { entity: feedEntity } = useSirenEntity({ href: selectedOrderEntity?.getSubEntityByRel('https://api.binsentry.com/rel/feed')?.href });

  const additionalVolume = useOrderVolume({ orderId: selectedOrderId });
  const additionalWeight = (feedEntity?.properties?.bulkDensity || 0) * additionalVolume;

  const baseLevelData = useGetExpectedVolumeInBarn({ additionalVolume, additionalWeight });

  return (
    <BarnFeedLevelDisplay
      {...baseLevelData}
      heightPx={BIN_PROFILE_HEIGHT_PX}
      widthPx={BIN_PROFILE_WIDTH_PX}
    />
  );
}

function useGetExpectedVolumeInBarn({ additionalVolume, additionalWeight }) {
  const [
    forecastsByBinId,
    selectedBarnEntity,
    selectedBarnsBins,
    selectedBarnOrganizationBinLevelsEntity,
    selectedBarnOrganizationBinEntity,
    isoDate,
  ] = useOrdersDialogStateSelector([
    'forecastsByBinId',
    'selectedBarnEntity',
    'selectedBarnsBins',
    'selectedBarnOrganizationBinLevelsEntity',
    'selectedBarnOrganizationBinEntity',
    'isoDate',
  ]);
  const barnId = selectedBarnEntity?.getLinkByRel('self').href;
  const measurementOverrides = useBinLevelMeasurementOverrides({ bin: selectedBarnOrganizationBinEntity });
  const binLevelMeasurement = useMeasurement({ name: 'binLevel', ...measurementOverrides });
  const { fullTotalWeight, loading: totalWeightLoading } = useBarnTotalWeight({ barnId });
  const latestBarnLevel = useBarnLatestFeedLevel({ barnId });

  const currentTimeLuxon = DateTime.fromISO(isoDate);
  const timeDistanceToLevel = Math.abs(
    DateTime.fromISO(selectedBarnOrganizationBinLevelsEntity?.properties?.publishedAt).diff(currentTimeLuxon).as('seconds'),
  );

  const selectedBinForecasts = selectedBarnsBins.map(entity => {
    const binId = entity.getLinkByRel('self').href;
    const forecast = forecastsByBinId[binId];
    if (!forecast) {
      return;
    }

    const timeDistance = Math.abs(
      DateTime.fromISO(forecast?.publishedAt).diff(currentTimeLuxon).as('seconds'),
    );
    return { binId, forecast, timeDistance };
  }).filter(item => item);

  const { totalVolume } = latestBarnLevel;

  // we trust the forecast to be more accurate than the binLevel when the forecast is nearer to the selected time
  const forecastNearer = selectedBinForecasts[0]?.timeDistance < timeDistanceToLevel;
  if (!totalWeightLoading && forecastNearer && selectedBinForecasts.length === selectedBarnsBins.length) {
    const volume = _.sumBy(selectedBinForecasts, 'forecast.low');
    const additionalLevel = binLevelMeasurement.convert({ volume: additionalVolume, weight: additionalWeight, totalVolume: totalVolume });
    const total = binLevelMeasurement.convert({ volume: totalVolume, weight: fullTotalWeight, totalVolume: totalVolume });

    return {
      ...latestBarnLevel,
      currentVolume: volume,
      additionalLevel,
      additionalVolume,
      total,
      unit: binLevelMeasurement.unit,
      loading: false,
    };
  }

  return {
    ...latestBarnLevel,
    loading: latestBarnLevel.loading || totalWeightLoading,
  };
}
